"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { Check, ShieldCheck, Layers, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface OverviewCopy {
  badge: string;
  standardsBadge: string;
  heading: string;
  lead: string;
  point1Title: string;
  point1Desc: string;
  point2Title: string;
  point2Desc: string;
  point3Title: string;
  point3Desc: string;
  ctaTechData: string;
  imgAlt: string;
}
const OVERVIEW_COPY: Record<Lang, OverviewCopy> = {
  en: {
    badge: "Screening & Classification",
    standardsBadge: "ISO 9044 / ASTM E2016",
    heading: "Engineered Separation Media for Extreme Particle Sizing and High-Capacity Dewatering",
    lead: "From sub-sieve micro-particle sizing to high-solids slurry clarification and fine aerosol demisting, our engineered stainless steel woven wire mesh and welded wedge wire screens maintain accurate cutoffs under sustained vibration, differential pressure, and abrasive flow.",
    point1Title: "V-Wire Self-Relieving Geometry",
    point1Desc: "Two-point particle contact prevents near-size blinding and maintains consistent hydraulic throughput in slurry dewatering and static sieve bend systems.",
    point2Title: "Calibrated Aperture Uniformity",
    point2Desc: "Precision square weaves and Dutch filter cloth adhere strictly to ASTM E2016 tolerances for sharp powder classification and zero media migration.",
    point3Title: "Rigid Wear-Resistant Fabrication",
    point3Desc: "High-yield resistance welds and tensioned edge preparations withstand cyclic mechanical vibration and heavy bed solids loads up to 45% w/w.",
    ctaTechData: "Technical Resources",
    imgAlt: "Stainless steel industrial screening and wedge wire separation media assembly"
  },
  zh: {
    badge: "筛分与物料分级",
    standardsBadge: "ISO 9044 / ASTM E2016 规范",
    heading: "工程级分离介质 专为严苛颗粒精密分级与大通量固液脱水设计",
    lead: "从微米级精细粉体筛分分级到高固含矿浆脱水与气流液沫消除，我们精工制造的不锈钢编织丝网与连续缝隙楔形网在持续振动冲击、工作压差与磨蚀流体中始终保持精确截留界限。",
    point1Title: "自洁防堵 V 型绕条几何结构",
    point1Desc: "两点触碰型线截面消除临界颗粒卡嵌嵌塞，在矿浆脱水与弧形筛分工况中维持恒定水力通量。",
    point2Title: "严苛标定的孔径公差一致性",
    point2Desc: "精密方孔编织与荷兰编织网严格符合 ASTM E2016 规范标准，确保清晰的粉体粒度分界与零介质脱落。",
    point3Title: "高强度抗磨振动装配",
    point3Desc: "精密电阻阻焊与高张力装配结构，耐受高频机械振动冲击与高达 45% w/w 的重固体床层负荷。",
    ctaTechData: "查阅技术规格资料",
    imgAlt: "不锈钢工业筛分与楔形丝网分离介质组件"
  },
  es: {
    badge: "Tamizado y clasificación",
    standardsBadge: "ISO 9044 / ASTM E2016",
    heading: "Medios de separación diseñados para clasificación exigente y deshidratación de gran capacidad",
    lead: "Desde la clasificación de micropartículas hasta la clarificación de lodos con alta carga y la eliminación de nieblas, nuestras mallas tejidas y cribas de alambre en cuña mantienen cortes precisos bajo vibración continua y flujo abrasivo.",
    point1Title: "Geometría en V autolimpiante",
    point1Desc: "El contacto en dos puntos evita la colmatación de partículas y mantiene un caudal constante en deshidratación de lodos y cribas curvas.",
    point2Title: "Uniformidad de apertura calibrada",
    point2Desc: "Tejidos cuadrados de precisión y telas Dutch conformes a las tolerancias ASTM E2016 para una clasificación nítida sin migración.",
    point3Title: "Fabricación rígida resistente al desgaste",
    point3Desc: "Soldadura por resistencia de alta tensión y acabados reforzados que soportan vibración mecánica cíclica y cargas de sólidos de hasta 45% p/p.",
    ctaTechData: "Recursos técnicos",
    imgAlt: "Conjunto de medios de separación de alambre en cuña y malla industrial de acero inoxidable"
  },
  de: {
    badge: "Siebung & Klassierung",
    standardsBadge: "ISO 9044 / ASTM E2016",
    heading: "Separationsmedien für anspruchsvolle Partikelklassierung und Hochleistungs-Entwässerung",
    lead: "Von der Mikropartikelklassierung über die Schlammentwässerung bis zur feinen Tropfenabscheidung gewährleisten unsere Drahtgewebe und Spaltsiebe exakte Trennschnitte unter Vibration und Druck.",
    point1Title: "Verstopfungsfreie V-Draht-Geometrie",
    point1Desc: "Zweipunktkontakt verhindert Verkeilung von Grenzkornpartikeln und sichert konstanten Durchsatz in Bogensieben und Entwässerungsanlagen.",
    point2Title: "Präzise kalibrierte Maschenweitenkonstanz",
    point2Desc: "Präzisions-Quadratmaschen und Tressengewebe nach ASTM E2016 für trennscharfe Pulverklassierung ohne Medienmigration.",
    point3Title: "Verschleißfeste, starre Konstruktion",
    point3Desc: "Hochfeste Widerstandsschweißung und spannstabile Randfassungen für zyklische Schwingungsbelastungen und Feststoffbeladungen bis 45 Gew.-%.",
    ctaTechData: "Technische Ressourcen",
    imgAlt: "Edelstahl-Industriesiebgewebe und Spaltsieb-Separationselemente"
  },
  fr: {
    badge: "Criblage & classification",
    standardsBadge: "ISO 9044 / ASTM E2016",
    heading: "Médias de séparation pour la classification précise et l'égouttage à haut débit",
    lead: "Du calibrage fin des poudres à la clarification de boues denses et l'élimination des brouillards, nos toiles métalliques et grilles à fil profilé garantissent des coupures nettes sous contraintes mécaniques.",
    point1Title: "Géométrie en V anticolmatante",
    point1Desc: "Le contact en deux points élimine le coincement des grains critiques et maintient un débit stable dans les grilles courbes et l'égouttage.",
    point2Title: "Régularité d'ouverture calibrée",
    point2Desc: "Toiles tissées de précision et toiles hollandaises conformes aux tolérances ASTM E2016 pour une coupure nette sans migration.",
    point3Title: "Conception rigide résistante à l'usure",
    point3Desc: "Soudures par résistance haute tenue et sertissages de rives conçus pour les vibrations cycliques et des charges jusqu'à 45 % m/m.",
    ctaTechData: "Ressources techniques",
    imgAlt: "Ensemble de toiles de criblage et grilles à fil profilé en acier inoxydable"
  },
  pt: {
    badge: "Peneiramento & classificação",
    standardsBadge: "ISO 9044 / ASTM E2016",
    heading: "Meios de separação para classificação rigorosa e desidratação de alta capacidade",
    lead: "Da classificação de partículas ultrafinas à clarificação de polpas de alta densidade e eliminação de névoas, nossas telas de arame tecidas e telas em cunha asseguram cortes precisos sob vibração e fluxo abrasivo.",
    point1Title: "Geometria em V autolimpante",
    point1Desc: "Contato em dois pontos evita a colmatação de partículas críticas e assegura vazão estável na desidratação e peneiras curvas.",
    point2Title: "Uniformidade de abertura calibrada",
    point2Desc: "Malhas quadradas de precisão e tramas holandesas estritamente em conformidade com ASTM E2016 para classificação sem migração.",
    point3Title: "Fabricação rígida resistente ao desgaste",
    point3Desc: "Soldas por resistência de alto rendimento e bordas pré-tensionadas para suportar vibrações cíclicas e cargas de sólidos de até 45% p/p.",
    ctaTechData: "Recursos técnicos",
    imgAlt: "Conjunto de telas de arame em cunha e malha industrial de aço inoxidável"
  }
};
export default function ScreeningOverview() {
  const {
    lang
  } = useLanguage();
  const c = pick(lang, OVERVIEW_COPY);
  const keyPoints = [{
    title: c.point1Title,
    desc: c.point1Desc
  }, {
    title: c.point2Title,
    desc: c.point2Desc
  }, {
    title: c.point3Title,
    desc: c.point3Desc
  }];
  return <section id="screeningoverview" data-nav="light" className="bg-muted/50 text-foreground border-b border-border mf-section">
      <div className="mf-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left 6 Columns: Large Framed Industrial Photo (no overlay, subtle zoom) */}
          <div className="lg:col-span-6">
            <Reveal className="w-full">
              <div className="mf-media-framed mf-ratio-card w-full mf-img-zoom">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screen.webp" alt={c.imgAlt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
              </div>
            </Reveal>
          </div>

          {/* Right 6 Columns: Narrative & Exactly Three Key Points */}
          <Reveal delay={80} className="lg:col-span-6 flex flex-col justify-center">
            <div className="mb-3 flex items-center gap-2">
              <Badge variant="outline" className="font-mono text-sm tracking-wide bg-card text-foreground">
                {c.badge}
              </Badge>
              <span className="text-sm font-mono text-muted-foreground flex items-center gap-1">
                <ShieldCheck className="size-3.5 text-primary inline" />
                {c.standardsBadge}
              </span>
            </div>

            <h2 className="font-default tracking-tight text-foreground mf-h2">
              {c.heading}
            </h2>

            <p className="mt-4 text-muted-foreground mf-lead max-w-[65ch]">
              {c.lead}
            </p>

            {/* Exactly Three Plain Key Points */}
            <div className="mt-6 flex flex-col gap-3.5">
              {keyPoints.map((point, index) => <Reveal key={point.title} delay={120 + index * 80}>
                  <div className="flex items-start gap-3.5 p-3.5 rounded-lg bg-card border border-border">
                    <span className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0 mt-0.5">
                      <Check className="size-5 stroke-[2.5]" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="font-default font-semibold text-base text-foreground leading-snug">
                        {point.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed mt-0.5">
                        {point.desc}
                      </p>
                    </div>
                  </div>
                </Reveal>)}
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="px-6 font-semibold">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="px-6 border-border bg-card hover:bg-muted font-semibold">
                <Link to="/TechnicalResourcesPage">
                  {c.ctaTechData}
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>;
}